import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { FUNDER_NAV } from "./FunderTerminal";
import { Sparkles, Loader2, X } from "lucide-react";
import { sdgLabel } from "../lib/sdg";
import { aiAutofillErrorMessage } from "../lib/aiAutofillError";

const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria","Kenya","Ghana","South Africa","Rwanda","Senegal","Tanzania","Uganda"];
const SDGS = ["SDG 2","SDG 3","SDG 4","SDG 5","SDG 7","SDG 8","SDG 9","SDG 13"];
const INSTRUMENTS = [
  { id: "equity", label: "Equity" },
  { id: "debt", label: "Debt" },
  { id: "convertible", label: "Convertible Note / SAFE" },
  { id: "mezzanine", label: "Mezzanine / Quasi-equity" },
  { id: "grant", label: "Grant (non-repayable)" },
  { id: "mix", label: "Mix (Equity + Debt)" },
  { id: "open", label: "Open to all structures" },
];
const RISK_OPTIONS = [
  { id: "bold", label: "The Bold Builder — higher risk, higher return" },
  { id: "balanced", label: "The Balanced Investor — medium risk" },
  { id: "steady", label: "The Steady Earner — lower risk" },
];
const DEAL_FOCUS = [
  { id: "businesses", label: "Businesses" },
  { id: "projects", label: "Projects" },
  { id: "both", label: "Both" },
];
const INVESTOR_TYPES = [
  { id: "individual", label: "Individual/Group" },
  { id: "institution", label: "Institution" },
];

export default function FunderMandate() {
  const [m, setM] = useState(null);
  const [msg, setMsg] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [thesis, setThesis] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => { (async()=>{ const r = await api.get("/funder/mandate"); setM(r.data); })(); }, []);
  if (!m) return <AppShell nav={FUNDER_NAV}>Loading…</AppShell>;
  const toggle = (key, val) => setM(s => ({...s, [key]: s[key].includes(val) ? s[key].filter(x=>x!==val) : [...s[key], val]}));
  const save = async () => {
    await api.put("/funder/mandate", m);
    setMsg("Mandate updated."); setTimeout(()=>setMsg(""), 2500);
  };

  const runAi = async () => {
    setAiBusy(true); setAiErr(""); setSuggestion(null);
    try {
      const { data } = await api.post("/funder/mandate/ai-suggest",
        { text: thesis }, { timeout: 90000 });
      setSuggestion(data);
    } catch (e) {
      setAiErr(aiAutofillErrorMessage(e, { kind: "thesis" }));
    } finally { setAiBusy(false); }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setM(prev => ({
      ...prev,
      sectors: suggestion.sectors || [],
      countries: suggestion.countries || [],
      stages: suggestion.stages || [],
      sdgs: suggestion.sdgs || [],
      ticket_min: suggestion.ticket_min ?? prev.ticket_min,
      ticket_max: suggestion.ticket_max ?? prev.ticket_max,
      min_readiness: suggestion.min_readiness ?? prev.min_readiness,
    }));
    setAiOpen(false); setSuggestion(null); setThesis("");
    setMsg("Mandate filled from thesis — review and save."); setTimeout(()=>setMsg(""), 4000);
  };

  return (
    <AppShell nav={FUNDER_NAV}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Investment mandate</h1>
          <p className="mt-1 text-sm text-neutral-600">Tell Clinq what to surface. Mandate-drift flags will be raised on off-criteria deals.</p>
        </div>
        <button data-testid="ai-mandate-open" onClick={()=>setAiOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-4 py-2 text-sm font-semibold text-[#0A8B85] hover:bg-[#0A8B85] hover:text-white">
          <Sparkles className="h-4 w-4"/> Auto-fill from thesis
        </button>
      </div>

      <div className="mt-6 space-y-6 rounded-2xl border border-neutral-200 bg-white p-6">
        <Block label="Investing as">
          <PillSelect opts={INVESTOR_TYPES} value={m.investor_type} onSelect={v=>setM({...m, investor_type: v})} testPrefix="mandate-investor"/>
        </Block>
        <Block label="Deal focus">
          <PillSelect opts={DEAL_FOCUS} value={m.deal_focus} onSelect={v=>setM({...m, deal_focus: v})} testPrefix="mandate-dealfocus"/>
        </Block>
        <Block label="Risk appetite">
          <PillSelect opts={RISK_OPTIONS} value={m.risk_appetite} onSelect={v=>setM({...m, risk_appetite: v})} testPrefix="mandate-risk"/>
        </Block>
        <Block label="Structures">
          <div className="flex flex-wrap gap-2">
            {INSTRUMENTS.map(o => {
              const on = (m.instruments || []).includes(o.id);
              return <button key={o.id} type="button" data-testid={`mandate-instrument-${o.id}`}
                onClick={() => setM(s => ({...s, instruments: on ? (s.instruments||[]).filter(x=>x!==o.id) : [...(s.instruments||[]), o.id]}))}
                className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
                {o.label}
              </button>;
            })}
          </div>
        </Block>
        <Block label="Sectors"><Chips opts={[...new Set([...SECTORS, ...(m.sectors||[])])]} selected={m.sectors} onToggle={v=>toggle("sectors",v)} testPrefix="mandate-sector"/></Block>
        <Block label="Countries"><Chips opts={[...new Set([...COUNTRIES, ...(m.countries||[])])]} selected={m.countries} onToggle={v=>toggle("countries",v)} testPrefix="mandate-country"/></Block>
        <Block label="Impact areas"><Chips opts={SDGS} selected={m.sdgs} onToggle={v=>toggle("sdgs",v)} renderLabel={sdgLabel} testPrefix="mandate-sdg"/></Block>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Ticket min (USD)">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
              <input data-testid="mandate-ticket-min" type="number" value={m.ticket_min} onChange={e=>setM({...m,ticket_min:+e.target.value})} className={inp + " pl-7"}/>
            </div>
          </Field>
          <Field label="Ticket max (USD)">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
              <input data-testid="mandate-ticket-max" type="number" value={m.ticket_max} onChange={e=>setM({...m,ticket_max:+e.target.value})} className={inp + " pl-7"}/>
            </div>
          </Field>
        </div>
        <div className="flex items-center justify-between border-t border-neutral-100 pt-5">
          {msg && <span className="text-sm font-semibold text-[#0A8B85]" data-testid="mandate-msg">{msg}</span>}
          <button data-testid="mandate-save" onClick={save} className="ml-auto rounded-lg bg-[#0A8B85] px-5 py-2 text-sm font-semibold text-white hover:bg-[#066667]">Save mandate</button>
        </div>
      </div>

      {/* AI assistant modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-mandate-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-neutral-200 bg-white px-6 py-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#0A8B85]"/>
                <h2 className="text-lg font-bold text-neutral-900">AI-assisted mandate builder</h2>
              </div>
              <button onClick={()=>{setAiOpen(false); setSuggestion(null); setAiErr("");}} className="rounded p-1 hover:bg-neutral-100" data-testid="ai-mandate-close"><X className="h-4 w-4"/></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-neutral-600">Paste your investment thesis (or a few paragraphs from your LP deck / fund memo). Clinq AI will extract sectors, geographies, ticket band and SDG focus.</p>
              <textarea data-testid="ai-thesis" rows={8} value={thesis} onChange={e=>setThesis(e.target.value)}
                placeholder="e.g. We are a $50M debt fund focused on West Africa, primarily Nigeria and Ghana. We back working-capital and revenue-stage SMEs in fintech, e-commerce and logistics. Average ticket: $300K–$1.2M. We prioritise gender-lens (SDG 5)…"
                className="mt-4 w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85] resize-none"/>
              {aiErr && <p className="mt-3 text-sm text-red-600" data-testid="ai-error">{String(aiErr)}</p>}

              <div className="mt-4 flex justify-end">
                <button data-testid="ai-mandate-run" disabled={aiBusy || thesis.length < 20} onClick={runAi}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                  {aiBusy ? <><Loader2 className="h-4 w-4 animate-spin"/>Analyzing thesis…</> : <><Sparkles className="h-4 w-4"/>Generate mandate</>}
                </button>
              </div>

              {suggestion && (
                <div className="mt-6 rounded-xl bg-[#F4F8F8] p-5" data-testid="ai-suggestion">
                  <div className="flex items-baseline justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">AI suggestion</p>
                    <span className="rounded-full bg-[#0A8B85]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0A8B85]">Confidence {suggestion.confidence}</span>
                  </div>
                  {suggestion.summary && <p className="mt-2 text-sm text-neutral-700">{suggestion.summary}</p>}
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <SuggBlock label="Sectors" vals={suggestion.sectors}/>
                    <SuggBlock label="Countries" vals={suggestion.countries}/>
                    <SuggBlock label="Stages" vals={suggestion.stages}/>
                    <SuggBlock label="Impact" vals={suggestion.sdgs} renderLabel={sdgLabel}/>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                    <Mini label="Ticket min" v={`$${(suggestion.ticket_min/1000).toFixed(0)}K`}/>
                    <Mini label="Ticket max" v={`$${(suggestion.ticket_max/1000).toFixed(0)}K`}/>
                    <Mini label="Min readiness" v={suggestion.min_readiness}/>
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={()=>setSuggestion(null)} className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Discard</button>
                    <button data-testid="ai-apply" onClick={applySuggestion} className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-semibold text-white hover:bg-[#066667]">Apply to mandate</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
const Block = ({label, children}) => (<div><p className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">{label}</p>{children}</div>);
const PillSelect = ({opts, value, onSelect, testPrefix}) => (
  <div className="flex flex-wrap gap-2">{opts.map(o => {
    const on = value === o.id;
    return <button key={o.id} type="button" data-testid={`${testPrefix}-${o.id}`} onClick={()=>onSelect(o.id)}
      className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>{o.label}</button>;
  })}</div>
);
const Chips = ({opts, selected, onToggle, renderLabel, testPrefix}) => (
  <div className="flex flex-wrap gap-2">{opts.map(o => {
    const on = selected.includes(o);
    return <button key={o} data-testid={`${testPrefix}-${o.replace(/\s+/g,'')}`} type="button" onClick={()=>onToggle(o)}
      className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>{renderLabel ? renderLabel(o) : o}</button>;
  })}</div>
);
const SuggBlock = ({label, vals, renderLabel}) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
    <div className="mt-1 flex flex-wrap gap-1.5">
      {(vals||[]).length === 0 ? <span className="text-xs text-neutral-400">— any —</span> :
        vals.map(v => <span key={v} className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-[#0A8B85] ring-1 ring-[#0A8B85]/20">{renderLabel ? renderLabel(v) : v}</span>)}
    </div>
  </div>
);
const Mini = ({label, v}) => (<div className="rounded-lg bg-white p-2.5"><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p><p className="mt-0.5 font-bold text-neutral-900">{v}</p></div>);
const inp = "w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]";
const Field = ({label, children}) => (<div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>{children}</div>);
